import {
  CHECKABLE_ENTITY_KINDS,
  type PulseFactCollectorInfo,
} from '@internal/backstage-plugin-pulse-common';

/**
 * Filter applied when a collect entry omits `entityFilter`; mirrors the
 * `entityFilter` declared by the catalog and HTTP fact collectors.
 */
export const COLLECTOR_DEFAULT_ENTITY_FILTER = [
  { kind: [...CHECKABLE_ENTITY_KINDS] },
];

const formatScalarValue = (value: unknown): string => {
  if (value === null) {
    return 'null';
  }

  if (value === undefined) {
    return '-';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value.toString();
  }

  if (typeof value === 'symbol') {
    return value.description ? `Symbol(${value.description})` : 'Symbol()';
  }

  return JSON.stringify(value);
};

const formatCollectorConfigValue = (
  value: unknown,
  indentLevel = 0,
): string[] => {
  const indent = '  '.repeat(indentLevel);

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return [`${indent}[]`];
    }

    return value.flatMap(item => {
      if (item !== null && typeof item === 'object') {
        return [
          `${indent}-`,
          ...formatCollectorConfigValue(item, indentLevel + 1),
        ];
      }

      return [`${indent}- ${formatScalarValue(item)}`];
    });
  }

  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);

    if (entries.length === 0) {
      return [`${indent}{}`];
    }

    return entries.flatMap(([key, nestedValue]) => {
      if (nestedValue !== null && typeof nestedValue === 'object') {
        return [
          `${indent}${key}:`,
          ...formatCollectorConfigValue(nestedValue, indentLevel + 1),
        ];
      }

      return [`${indent}${key}: ${formatScalarValue(nestedValue)}`];
    });
  }

  return [`${indent}${formatScalarValue(value)}`];
};

const formatHourAndMinute = (hour: number, minute: number): string => {
  const normalizedHour = ((hour % 24) + 24) % 24;
  const period = normalizedHour >= 12 ? 'PM' : 'AM';
  const twelveHour = normalizedHour % 12 || 12;

  return `${twelveHour.toString().padStart(2, '0')}:${minute
    .toString()
    .padStart(2, '0')} ${period}`;
};

const tryFormatCronHumanReadable = (cronExpression: string): string => {
  const cronParts = cronExpression.trim().split(/\s+/);

  if (cronParts.length !== 5) {
    return '';
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = cronParts;

  if (
    /^\d+$/.test(minute) &&
    /^\d+$/.test(hour) &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    return `At ${formatHourAndMinute(Number(hour), Number(minute))}`;
  }

  if (
    minute === '*' &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    return 'Every minute';
  }

  if (
    /^\*\/\d+$/.test(minute) &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    const minutes = minute.slice(2);
    return `Every ${minutes} minute${minutes === '1' ? '' : 's'}`;
  }

  if (
    minute === '0' &&
    /^\*\/\d+$/.test(hour) &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    const hours = hour.slice(2);
    return `Every ${hours} hour${hours === '1' ? '' : 's'}`;
  }

  return '';
};

export const formatCollectorConfig = (
  collectorConfig: PulseFactCollectorInfo['collectorConfig'],
): string => formatCollectorConfigValue(collectorConfig).join('\n');

const isEntityFilterGroup = (
  value: unknown,
): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const formatEntityFilterGroup = (group: Record<string, unknown>): string =>
  Object.entries(group)
    .map(([key, value]) => {
      const values = Array.isArray(value) ? value : [value];
      return `${key}: ${values.map(formatScalarValue).join(', ')}`;
    })
    .join(' and ');

export const formatEntityFilter = (entityFilter: unknown): string => {
  const groups = Array.isArray(entityFilter) ? entityFilter : [entityFilter];

  return groups
    .filter(isEntityFilterGroup)
    .map(formatEntityFilterGroup)
    .filter(group => group.length > 0)
    .join(' or ');
};

/**
 * Resolves the entities a collect entry includes, falling back to the
 * collector's own default filter when `entityFilter` is not configured.
 */
export const formatIncludedEntities = (entityFilter: unknown): string =>
  formatEntityFilter(entityFilter) ||
  formatEntityFilter(COLLECTOR_DEFAULT_ENTITY_FILTER);

export const formatScheduleFrequency = (
  frequency: PulseFactCollectorInfo['schedule']['frequency'],
): string => {
  if (frequency === undefined || frequency === null) {
    return '-';
  }

  const cronValue = (frequency as { cron?: unknown }).cron;
  if (typeof cronValue === 'string') {
    const humanReadable = tryFormatCronHumanReadable(cronValue);
    return humanReadable
      ? `cron(${cronValue}) ${humanReadable}`
      : `cron(${cronValue})`;
  }

  return formatScalarValue(frequency);
};
